import { ButtonV2, Checkbox, Spinner } from '@cimplico/components';
import { NotificationPreferenceType } from '../../../../types/notification';
import { useUpdateNotificationPreferences } from '../../../../queries/use-update-notification-preferences';
import { toastNotification } from '../../../../components/toast/Toast';
import { allNotificationEvents } from './UserNotificationsContent';
import { useFirmContext } from '../../../../providers/FirmProvider';
import { useNavigate } from 'react-router-dom';
import { useIsFirmRole } from '../../../../hooks/use-is-firm-role';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import { useUserNotificationPreferences } from '../../../../queries/use-notification.preferences';
import { useIsActiveSubscription } from '../../../../hooks/use-is-active-subscription';
import SectionLabel from '../../../../components/section-label/SectionLabel';
import ErrorSplashScreen from '../../../../pages/ErrorSplashScreen';
import SettingsContainer from '../../../../components/settings-container/SettingsContainer';
import { useHasFeatureAccess } from '../../../../hooks/use-has-feature-access';

function Preview(): JSX.Element {
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const { isAdmin } = useIsFirmRole();

    return (
        <div className="flex flex-col justify-center items-center absolute z-40 top-1/2 right-1/2 translate-x-1/2 translate-y-1/2 bg-white/90 py-4 px-6 rounded-md shadow-md w-[50%]">
            <LockClosedIcon className="h-6 w-6 text-gray-600" />
            <p className="text-gray-600 mb-2 text-center">
                Upgrade to the <b>Professional</b> plan to access this feature
            </p>
            <ButtonV2
                size="sm"
                label="Upgrade"
                leftIcon={<ArrowUpCircleIcon />}
                onClick={(): void => {
                    navigate(`/${shortId}/settings`);
                }}
                isDisabled={!isAdmin}
            />
            {!isAdmin && <p className="text-gray-600 mt-2 text-sm">To upgrade, please contact your firm&apos;s administrator</p>}
        </div>
    );
}

function UserNotifications(): JSX.Element {
    const { shortId } = useFirmContext();
    const activeSubscription = useIsActiveSubscription();
    const featureAccess = { canAccessUserNotifications: useHasFeatureAccess('user-notifications:read') };

    const notificationPreferencesQuery = useUserNotificationPreferences();
    const updateNotificationPreferencesQuery = useUpdateNotificationPreferences();

    if (notificationPreferencesQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (notificationPreferencesQuery.isError || !notificationPreferencesQuery.isSuccess) {
        if (notificationPreferencesQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="User notification preferences not found."
                    redirectTitle="Back to users"
                    redirectUrl={`/${shortId}/users`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to users"
                redirectUrl={`/${shortId}/users`}
                leftArrow={true}
            />
        );
    }

    function handleChecked(type: NotificationPreferenceType, subscribe: boolean): void {
        updateNotificationPreferencesQuery.mutate([{ type, subscribe }], {
            onError: () => {
                toastNotification('error', 'Unable to update notification preference configuration!');
            },
        });
    }

    const preferencesConfig = notificationPreferencesQuery.data.preferences.config;

    return (
        <div className="grid gap-y-4">
            {!featureAccess.canAccessUserNotifications && <Preview />}
            <div className={`${!featureAccess.canAccessUserNotifications ? 'blur-sm' : ''}`}>
                {Object.keys(allNotificationEvents).map((category, idx) => {
                    const categoryEvents = allNotificationEvents[category].features;
                    return (
                        <SettingsContainer key={idx} isLastItem={idx === Object.keys(allNotificationEvents).length - 1}>
                            <SectionLabel
                                title={allNotificationEvents[category].title}
                                description={allNotificationEvents[category].description}
                            />
                            <div className="grid gap-y-6">
                                {Object.keys(categoryEvents).map((event) => {
                                    const matchingRecord = preferencesConfig[category].find((c) => c.type === event);
                                    if (!matchingRecord) {
                                        return <></>;
                                    }
                                    return (
                                        <section className="flex gap-3 items-start pt-2" key={event}>
                                            <div className="pt-0.5">
                                                <Checkbox
                                                    disabled={!featureAccess.canAccessUserNotifications || !activeSubscription}
                                                    checked={matchingRecord.enabled}
                                                    onChange={(): void => handleChecked(matchingRecord.type, !matchingRecord.enabled)}
                                                />
                                            </div>
                                            <div className="text-xs">
                                                <label className="font-medium text-gray-600 dark:text-gray-400">
                                                    {categoryEvents[event].title}
                                                </label>
                                                <p className="font-normal text-gray-500">{categoryEvents[event].description}</p>
                                            </div>
                                        </section>
                                    );
                                })}
                            </div>
                        </SettingsContainer>
                    );
                })}
            </div>
        </div>
    );
}

export default UserNotifications;
